// 受信した映像・音声トラックの DOM (<video> / <audio>) 管理。
// videoByStreamId / metadataByStreamId は他モジュール (レイアウト計算・room/free
// モード) からも参照されるため公開する。レイアウト再計算は注入されたコールバック
// (refreshLayout / updateLayoutAspect) 経由で呼ぶ (循環参照を避ける)。

import * as annotations from "./annotations.js";
import { t } from "./i18n.js";
import type { WindowMetadata } from "@wincast/shared";

export const metadataByStreamId = new Map<string, WindowMetadata>();
export const videoByStreamId = new Map<string, HTMLVideoElement>();

type MediaEls = {
  videoGrid: HTMLDivElement;
  remoteAudio: HTMLAudioElement;
  audioMuteBtn: HTMLButtonElement;
};

let els: MediaEls;
let refreshLayout: () => void;
let updateLayoutAspect: () => void;

export function initMediaElements(
  deps: MediaEls,
  callbacks: { refreshLayout: () => void; updateLayoutAspect: () => void },
): void {
  els = deps;
  refreshLayout = callbacks.refreshLayout;
  updateLayoutAspect = callbacks.updateLayoutAspect;
}

export function clearVideos(): void {
  for (const video of videoByStreamId.values()) {
    annotations.detachVideo(video);
    video.srcObject = null;
    video.remove();
  }
  videoByStreamId.clear();
  metadataByStreamId.clear();
  refreshLayout();
}

export function attachVideoForStream(stream: MediaStream): void {
  let video = videoByStreamId.get(stream.id);
  if (!video) {
    video = document.createElement("video");
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;
    // フェードイン制御:
    // - meta-ready: 個々の video が loadedmetadata を受け取ったか
    // - ready    : 実際に表示 (CSS animation) を始めて良いか
    // ホストが複数ウィンドウを共有している場合、各 track の loadedmetadata は
    // ばらつくため、全てが meta-ready になってから一斉に ready=true に切替える。
    video.dataset.ready = "false";
    video.dataset.metaReady = "false";
    const meta = metadataByStreamId.get(stream.id);
    if (meta) {
      video.title = `${meta.windowTitle} (${meta.originalWidth}×${meta.originalHeight})`;
      video.dataset.index = String(meta.index);
      // index ベースの view-transition-name。レイアウト切替時に
      // 同じ name を持つ old/new 要素が対応付けされて位置補間が走る。
      video.style.setProperty(
        "view-transition-name",
        `wincast-vid-${meta.index}`,
      );
    }
    // videoWidth/Height は最初の loadedmetadata で確定する。
    // この時点で実解像度ベースで layout aspect を再計算する。
    const videoEl = video;
    video.addEventListener("loadedmetadata", () => {
      updateLayoutAspect();
      videoEl.dataset.metaReady = "true";
      maybeRevealAllVideos();
      // 実解像度が判明したのでアノテーション overlay の viewBox を更新
      annotations.updateOriginalSize(
        videoEl,
        videoEl.videoWidth,
        videoEl.videoHeight,
      );
    });
    video.addEventListener("resize", () => {
      updateLayoutAspect();
      annotations.updateOriginalSize(
        videoEl,
        videoEl.videoWidth,
        videoEl.videoHeight,
      );
    });
    videoByStreamId.set(stream.id, video);
    els.videoGrid.appendChild(video);
    // アノテーション overlay を attach。meta があれば originalWidth/Height、
    // 無ければ後で updateOriginalSize() で差し替え。
    annotations.attachVideo(
      video,
      meta?.originalWidth ?? 0,
      meta?.originalHeight ?? 0,
    );
    refreshLayout();
  }
  if (video.srcObject !== stream) {
    video.srcObject = stream;
  }

  stream.addEventListener("removetrack", () => removeIfEmpty(stream));
  for (const track of stream.getTracks()) {
    track.addEventListener("ended", () => removeIfEmpty(stream));
  }
}

// 現在 attach されている全 video が loadedmetadata を受け取ったら
// 一斉に ready=true に切替えてフェードインを発火させる。
// 既に ready=true の video には何もしない (animation 再走を防ぐ)。
// 新たな track が遅れて追加された場合は、その track の loadedmetadata で
// 再度この関数が呼ばれ、新規 video だけが追加でフェードインする。
function maybeRevealAllVideos(): void {
  const all = Array.from(videoByStreamId.values());
  if (all.length === 0) return;
  const allMetaReady = all.every((v) => v.dataset.metaReady === "true");
  if (!allMetaReady) return;
  for (const v of all) {
    if (v.dataset.ready !== "true") {
      v.dataset.ready = "true";
    }
  }
}

function removeIfEmpty(stream: MediaStream): void {
  if (stream.getTracks().some((t) => t.readyState === "live")) return;
  const video = videoByStreamId.get(stream.id);
  if (video) {
    annotations.detachVideo(video);
    video.srcObject = null;
    video.remove();
    videoByStreamId.delete(stream.id);
    refreshLayout();
  }
  metadataByStreamId.delete(stream.id);
}

export function attachAudioTrack(track: MediaStreamTrack): void {
  const stream = new MediaStream([track]);
  els.remoteAudio.srcObject = stream;
  // 接続ボタンクリック起点のためミュート無しで再生可能 (自動再生ポリシー)
  els.remoteAudio.muted = els.audioMuteBtn.dataset.muted === "true";
  void els.remoteAudio.play().catch(() => {
    // 万一ブロックされたらミュート状態に倒してリトライ
    els.remoteAudio.muted = true;
    setAudioMuted(true);
    void els.remoteAudio.play().catch(() => {});
  });
  els.audioMuteBtn.hidden = false;

  track.addEventListener("ended", () => {
    detachAudioTrack();
  });
}

export function detachAudioTrack(): void {
  els.remoteAudio.srcObject = null;
  els.audioMuteBtn.hidden = true;
}

export function setAudioMuted(muted: boolean): void {
  els.remoteAudio.muted = muted;
  els.audioMuteBtn.dataset.muted = muted ? "true" : "false";
  // SVG (volume-on / volume-off) は CSS で data-muted に応じて表示切替するので
  // textContent を書き換える必要はない。aria-label / title だけ更新。
  const label = muted ? t("toolbar.muteOff") : t("toolbar.muteOn");
  els.audioMuteBtn.setAttribute("aria-label", label);
  els.audioMuteBtn.title = label;
}
